"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import {
  register, login as loginFlow, requestPasswordReset, resetPassword,
  resendVerification,
} from "@/lib/auth/flows";
import {
  destroySession, resolveSession, cookieOptions, requestIsSecure, SESSION_COOKIE,
} from "@/lib/auth/session";

/*
 * Server actions for the auth screens.
 *
 * Each returns a plain { error } for the form to render, or redirects. None
 * returns anything that distinguishes "no such account" from "wrong password" --
 * the flows layer already collapses those, and the actions must not undo it by
 * being helpful.
 */

export type FormState = { error?: string; done?: boolean };

function safeNext(next: FormValue): string {
  const s = typeof next === "string" ? next : "";
  // Only same-site paths. Reflecting an arbitrary ?next= into a redirect is an
  // open-redirect: an attacker sends a real login link that bounces to their
  // own page afterwards.
  return s.startsWith("/") && !s.startsWith("//") ? s : "/";
}

type FormValue = FormDataEntryValue | null;
const str = (v: FormValue) => (typeof v === "string" ? v.trim() : "");

export async function signUpAction(_prev: FormState, form: FormData): Promise<FormState> {
  const name = str(form.get("name"));
  const email = str(form.get("email"));
  const password = typeof form.get("password") === "string" ? String(form.get("password")) : "";
  const kind = str(form.get("kind")) === "practitioner" ? "practitioner" : "client";
  const company = str(form.get("company"));

  if (!name) return { error: "Enter your name" };

  const r = await register({ email, name, password, kind, company });
  if (!r.ok) return { error: r.error };

  // Deliberately does NOT sign the user in. The design requires a confirmed
  // address before payment or application review, and signing in immediately
  // teaches people to ignore the email.
  redirect(`/verify/pending?email=${encodeURIComponent(email)}`);
}

export async function loginAction(_prev: FormState, form: FormData): Promise<FormState> {
  const email = str(form.get("email"));
  const password = typeof form.get("password") === "string" ? String(form.get("password")) : "";
  const next = safeNext(form.get("next"));

  const r = await loginFlow(email, password);
  if (!r.ok) return { error: r.error };

  (await cookies()).set(SESSION_COOKIE, r.token, cookieOptions(r.expiresAt, await requestIsSecure()));

  /*
   * Land people in their own portal.
   *
   * `next` wins when there is one -- somebody sent to /login from a deep link
   * should arrive where they were going. With no destination, everyone was
   * dropped on the marketing homepage, which is the one page in the product
   * that has nothing for a signed-in person on it.
   *
   * The role is derived from the session, not stored, so this is the same
   * answer the layouts use to decide who may enter.
   */
  if (next !== "/") redirect(next);

  const user = await resolveSession(r.token);
  switch (user?.actor.role) {
    case "admin": redirect("/admin");
    case "consultant": redirect("/consultant");
    case "trainee": redirect("/trainee/path");
    default: redirect("/portal");
  }
}

export async function logoutAction(): Promise<void> {
  const jar = await cookies();
  await destroySession(jar.get(SESSION_COOKIE)?.value);
  jar.delete(SESSION_COOKIE);
  redirect("/login");
}

export async function requestResetAction(_prev: FormState, form: FormData): Promise<FormState> {
  await requestPasswordReset(str(form.get("email")));
  // Always the same outcome. The screen says so, and there is nothing here to
  // branch on because requestPasswordReset returns void by design.
  return { done: true };
}

export async function resetPasswordAction(_prev: FormState, form: FormData): Promise<FormState> {
  const token = str(form.get("token"));
  const password = typeof form.get("password") === "string" ? String(form.get("password")) : "";
  const confirm = typeof form.get("confirm") === "string" ? String(form.get("confirm")) : "";

  if (password !== confirm) return { error: "Those passwords do not match" };

  const r = await resetPassword(token, password);
  if (!r.ok) return { error: r.error };
  redirect("/login?reset=1");
}

export async function resendVerificationAction(_prev: FormState, form: FormData): Promise<FormState> {
  await resendVerification(str(form.get("email")));
  return { done: true };
}
